import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { timeout, catchError, map, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthStateService } from './auth-state.service';
import { ApiMembresiaResponse } from '../types/API.types';
import {
  CheckPhoneAvailabilityResponse,
  ValidateEmailResponse,
  User,
} from '../types/auth.types';
import {
  LOCAL_STORAGE_KEY_AUTH_TOKEN,
  LOCAL_STORAGE_KEY_GARAGE_VEHICLES,
  LOCAL_STORAGE_KEY_USER_DATA,
} from '../config/variables';
import { TipoProveedorOauth } from '../constants/tipo-proveedor-oauth.enum';
import { TipoVerificacion } from '../constants/tipo-verificacion.enum';

// Interface para el login
export interface LoginPayload {
  identificador: string;
  password?: string; // Para login con contraseña
  tipoVerificacion?: number; // Opcional - solo para login con OTP
  idVerificacion?: number; // Opcional - solo para login con OTP
  otp?: string; // Para login con OTP
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Interfaces para OTP
export interface GenerateOtpPayload {
  tipoVerificacion: number;
  destino: string; // el valor siempre debe ser un string, aunque sea un id numerico
}

export interface GenerateOtpResponse {
  idVerificacion: number;
}

// Interfaces para verificación OTP
export interface VerifyOtpPayload {
  idVerificacion: number;
  otp: string;
}

export interface VerifyOtpResponse {
  idVerificacion: number;
}

// Interfaces para registro
export interface RegisterPayload {
  nombre: string;
  email: string;
  prefijoTelefono: string;
  telefono: string;
  idProveedor: number;
  modoRegistro: number;
  password: string;
  idToken: string;
  idUsuarioProveedor: string;
  photoUrl: string | null;
  otp: string | null;
  idVerificacion: number | null;
  idEmail?: number;
  origen?: 'web' | 'app';
}

export interface RegisterResponse {
  token: string;
  user: User;
}

// Interfaces para social login
export interface SocialLoginPayload {
  idProveedor: TipoProveedorOauth; // 'google', 'facebook', 'apple', etc.
  idToken: string; // identificador unico del usuario en el proveedor
  identificador?: string; // Email opcional
  accessToken?: string; // Access token opcional
}

export interface SocialLoginResponse {
  token: string;
  user: User;
}

// Interface para actualizar sucursal preferida
export interface UpdateSucursalPreferidaPayload {
  sucursalPreferida: string;
}

export interface UpdateSucursalPreferidaResponse {
  success: boolean;
  message: string;
  data?: {
    sucursalPreferida: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class EmailValidationService {
  private apiUrl = `${environment.apiUrl}/${environment.apiVersion}`;

  constructor(
    private http: HttpClient,
    private authStateService: AuthStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  // Método para limpiar datos del usuario (útil antes de un nuevo login)
  clearUserData(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    localStorage.removeItem(LOCAL_STORAGE_KEY_AUTH_TOKEN);
    localStorage.removeItem(LOCAL_STORAGE_KEY_USER_DATA);
    localStorage.removeItem(LOCAL_STORAGE_KEY_GARAGE_VEHICLES);
    sessionStorage.removeItem('totpSecret');
  }

  // NOTE: REFACTOR HECHO
  validateEmailInUse(
    email: string
  ): Observable<ApiMembresiaResponse<ValidateEmailResponse>> {
    const url = `${this.apiUrl}${environment.endpoints.validateEmailInUse}`;
    const params = { email };

    return this.http
      .get<ApiMembresiaResponse<ValidateEmailResponse>>(url, { params })
      .pipe(
        timeout(10000), // 10 segundos de timeout
        map((response) => {
          console.log('📡 Servicio - Respuesta del servidor:', response);
          return response;
        }),
        catchError((error) => {
          console.log('🔴 Servicio - Error capturado:', error);
          console.log(
            '🔄 Servicio - Retornando respuesta de error del API:',
            error.error
          );
          return of(error.error as ApiMembresiaResponse<ValidateEmailResponse>);
        })
      );
  }

  // NOTE: REFACTOR HECHO
  login(
    payload: LoginPayload
  ): Observable<ApiMembresiaResponse<LoginResponse>> {
    const url = `${this.apiUrl}${environment.endpoints.login}`;

    // Limpiar datos anteriores antes de hacer login para evitar conflictos
    // entre diferentes usuarios que inicien sesión en la misma sesión del navegador
    this.clearUserData();

    console.log('🔐 Login - Enviando petición:', url, payload);

    return this.http
      .post<ApiMembresiaResponse<LoginResponse>>(url, payload)
      .pipe(
        timeout(10000), // 10 segundos de timeout
        map((response: ApiMembresiaResponse<LoginResponse>) => {
          console.log('✅ Login exitoso:', response);

          // Si el login es exitoso y tenemos token y usuario, guardarlos
          if (response?.success && response?.data) {
            // Limpiar datos anteriores antes de guardar los nuevos
            this.clearUserData();

            // Guardar token
            if (response.data.token) {
              localStorage.setItem('authToken', response.data.token);
              console.log('🎫 Token guardado');
            }

            // Guardar información del usuario
            if (response.data.user) {
              localStorage.setItem(
                'userData',
                JSON.stringify(response.data.user)
              );
              console.log('👤 Datos de usuario guardados:', response.data.user);
            }

            // Guardar el totpSecret si existe (para futuras autenticaciones)
            if (response.data.user?.totpSecret) {
              sessionStorage.setItem(
                'totpSecret',
                response.data.user.totpSecret
              );
            }

            // Notificar cambio de estado de autenticación
            this.authStateService.notifyLogin();
          }

          return response;
        }),
        catchError((error) => {
          console.error('❌ Error en login:', error);
          console.log(
            '🔄 Servicio - Retornando respuesta de error del API:',
            error.error
          );
          return of(error.error as ApiMembresiaResponse<LoginResponse>);
        })
      );
  }

  // NOTE: REFACTOR HECHO
  // Método para social login (OAuth)
  socialLogin(
    payload: SocialLoginPayload
  ): Observable<ApiMembresiaResponse<SocialLoginResponse>> {
    const url = `${this.apiUrl}${environment.endpoints.socialLogin}`;

    // Limpiar datos anteriores antes de hacer social login para evitar conflictos
    // entre diferentes usuarios que inicien sesión en la misma sesión del navegador
    this.clearUserData();

    console.log('🔐 Social Login - Enviando petición:', url, payload);

    return this.http
      .post<ApiMembresiaResponse<SocialLoginResponse>>(url, payload)
      .pipe(
        timeout(15000), // 15 segundos para social login
        map((response: ApiMembresiaResponse<SocialLoginResponse>) => {
          console.log('✅ Social Login exitoso:', response);

          // Si el social login es exitoso y tenemos token y usuario, guardarlos
          if (response?.success && response?.data) {
            // Limpiar datos anteriores antes de guardar los nuevos
            this.clearUserData();

            // Guardar token
            if (response.data.token) {
              localStorage.setItem('authToken', response.data.token);
              console.log('🎫 Token de social login guardado');
            }

            // Guardar información del usuario
            if (response.data.user) {
              localStorage.setItem(
                'userData',
                JSON.stringify(response.data.user)
              );
              console.log(
                '👤 Datos de usuario de social login guardados:',
                response.data.user
              );
            }

            // Guardar el totpSecret si existe (para futuras autenticaciones)
            if (response.data.user?.totpSecret) {
              sessionStorage.setItem(
                'totpSecret',
                response.data.user.totpSecret
              );
            }

            // Notificar cambio de estado de autenticación
            this.authStateService.notifyLogin();
          }

          return response;
        }),
        catchError((error) => {
          console.error('❌ Error en social login:', error);
          console.log(
            '🔄 Servicio - Retornando respuesta de error del API:',
            error.error
          );
          return of(error.error as ApiMembresiaResponse<SocialLoginResponse>);
        })
      );
  }

  // Método auxiliar para obtener el usuario actual
  getCurrentUser(): User | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  }

  // Método para cerrar sesión
  logout(): void {
    this.clearUserData();

    // Notificar cambio de estado de autenticación
    this.authStateService.notifyLogout();
  }

  // NOTE: REFACTOR HECHO
  // Método para generar OTP
  generateOtp(payload: GenerateOtpPayload) {
    const url = `${this.apiUrl}${environment.endpoints.generateOtp}`;

    console.log('📱 Generando OTP:', url, payload);

    return this.http
      .post<ApiMembresiaResponse<GenerateOtpResponse>>(url, payload)
      .pipe(
        timeout(10000),
        map((response: ApiMembresiaResponse<GenerateOtpResponse>) => {
          console.log('✅ OTP generado exitosamente:', response);
          return response;
        }),
        catchError((error) => {
          console.error('❌ Error generando OTP:', error);
          console.log(
            '🔄 Servicio - Retornando respuesta de error del API:',
            error.error
          );
          return of(error.error as ApiMembresiaResponse<GenerateOtpResponse>);
        })
      );
  }

  // NOTE: REFACTOR HECHO
  // Método para registrar usuario
  register(payload: RegisterPayload) {
    const url = `${this.apiUrl}${environment.endpoints.register}`;

    console.log('📝 Registrando usuario:', url, payload);

    return this.http
      .post<ApiMembresiaResponse<RegisterResponse>>(url, {
        ...payload,
        origen: 'web',
      })
      .pipe(
        timeout(15000), // 15 segundos para registro
        map((response: ApiMembresiaResponse<RegisterResponse>) => {
          console.log('✅ Usuario registrado exitosamente:', response);

          // Si el registro es exitoso, guardar datos como en login
          if (response?.success && response?.data) {
            if (response.data.token) {
              localStorage.setItem('authToken', response.data.token);
              console.log('🎫 Token de registro guardado');
            }

            if (response.data.user) {
              localStorage.setItem(
                'userData',
                JSON.stringify(response.data.user)
              );
              console.log('👤 Datos de usuario nuevo guardados');
            }
          }

          return response;
        }),
        catchError((error) => {
          console.error('❌ Error en registro:', error);
          console.log(
            '🔄 Servicio - Retornando respuesta de error del API:',
            error.error
          );
          return of(
            error.error as ApiMembresiaResponse<CheckPhoneAvailabilityResponse>
          );
        })
      );
  }

  // Método específico para generar OTP SMS para usuarios existentes
  generateSmsOtpForExistingUser({
    idTelefono,
  }: {
    idTelefono: number;
  }): Observable<ApiMembresiaResponse<GenerateOtpResponse>> {
    const payload: GenerateOtpPayload = {
      tipoVerificacion: environment.verificationType.LOGIN_CON_TELEFONO,
      destino: idTelefono.toString(),
    };

    console.log('📱 Generando SMS OTP para usuario existente:', payload);
    return this.generateOtp(payload);
  }

  // Método específico para generar OTP EMAIL para usuarios existentes
  generateEmailOtpForExistingUser({
    email,
  }: {
    email: string;
  }): Observable<ApiMembresiaResponse<GenerateOtpResponse>> {
    const payload: GenerateOtpPayload = {
      tipoVerificacion: environment.verificationType.LOGIN_CON_EMAIL,
      destino: email,
    };

    console.log('📧 Generando EMAIL OTP para usuario existente:', payload);
    return this.generateOtp(payload);
  }

  // Método para login con OTP
  loginWithOtp({
    email,
    otp,
    idVerificacion,
    tipoVerificacion,
  }: {
    email: string;
    otp: string;
    idVerificacion: number;
    tipoVerificacion: TipoVerificacion;
  }) {
    return this.login({
      identificador: email,
      tipoVerificacion: tipoVerificacion,
      idVerificacion: idVerificacion,
      otp: otp,
    });
  }

  // Método para verificar OTP
  verifyOtp(payload: VerifyOtpPayload): Observable<VerifyOtpResponse> {
    const url = `${this.apiUrl}${environment.endpoints.verifyOtp}`;

    console.log('🔍 Verificando OTP:', url, payload);

    return this.http.post<VerifyOtpResponse>(url, payload).pipe(
      timeout(10000),
      map((response: VerifyOtpResponse) => {
        console.log('✅ OTP verificado exitosamente:', response);
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error verificando OTP:', error);
        return throwError(() => error);
      })
    );
  }

  // Verificar si el teléfono está disponible
  checkPhoneAvailability(telefono: string, prefijo: string = '+504') {
    const url = `${environment.apiUrl}/v1/auth/revisar-telefono-disponible`;
    const params = new HttpParams()
      .set('telefono', telefono)
      .set('prefijo', prefijo);

    return this.http.get<ApiMembresiaResponse<CheckPhoneAvailabilityResponse>>(
      url,
      { params }
    );
  }

  // Método para obtener información del usuario autenticado
  getMe() {
    const url = `${this.apiUrl}/auth/me`;

    // Obtener el token del localStorage
    const token = localStorage.getItem('authToken');

    if (!token) {
      console.warn('⚠️ No hay token de autenticación disponible');
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http.get<ApiMembresiaResponse<User>>(url, { headers }).pipe(
      timeout(10000), // 10 segundos de timeout
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  // Método para actualizar sucursal preferida
  updateSucursalPreferida(
    payload: UpdateSucursalPreferidaPayload
  ): Observable<UpdateSucursalPreferidaResponse> {
    const url = `${this.apiUrl}/stores/preferida`;

    // Obtener el token del localStorage
    const token = localStorage.getItem(LOCAL_STORAGE_KEY_AUTH_TOKEN);

    if (!token) {
      console.warn('⚠️ No hay token de autenticación disponible');
      return throwError(() => new Error('No hay token de autenticación'));
    }

    // Configurar headers con el token de autorización
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    return this.http
      .patch<UpdateSucursalPreferidaResponse>(url, payload, { headers })
      .pipe(
        timeout(10000), // 10 segundos de timeout
        map((response: UpdateSucursalPreferidaResponse) => {
          return response;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }
}
